import { TimerMode } from '@/components/settings/timer-configuration/types'
import { GameroomEvent } from '@/redux/types/room'
import { PhaseType, RoundConfig } from '@/redux/slices/timerConfigurationSlice'
import { getPhaseTypesForRound } from '@/utils/timelineUtils'

export const OTHER_ROUND_KEY = 0
export const UNRELEASED_GROUP_KEY = -1

export interface EventRoundGroup {
  roundKey: number
  roundLabel: string
  events: GameroomEvent[]
}

export interface GroupedEvents {
  unreleased: GameroomEvent[]
  roundGroups: EventRoundGroup[]
}

export interface GroupEventsByRoundOptions {
  includeUnreleased?: boolean
  scheduledReleaseByEventId?: Map<number, number>
}

export function getRoundLabel(roundKey: number): string {
  return roundKey === OTHER_ROUND_KEY ? 'Other' : `Round ${roundKey}`
}

export function buildScheduledReleaseRoundByEventId(
  roundConfigurations: RoundConfig[] | undefined
): Map<number, number> {
  const map = new Map<number, number>()
  for (const round of roundConfigurations ?? []) {
    if (!round.autoReleaseEnabled) continue
    const roundNumber = round.roundNumber ?? 0
    if (roundNumber < 1) continue
    for (const eventId of round.autoReleaseEventIds ?? []) {
      map.set(eventId, roundNumber)
    }
  }
  return map
}

function addToRoundBucket(
  byRound: Map<number, GameroomEvent[]>,
  roundKey: number,
  event: GameroomEvent
) {
  const list = byRound.get(roundKey) ?? []
  list.push(event)
  byRound.set(roundKey, list)
}

export function groupEventsByRound(
  events: GameroomEvent[],
  options?: GroupEventsByRoundOptions
): GroupedEvents {
  const includeUnreleased = options?.includeUnreleased ?? false
  const scheduledReleaseByEventId =
    options?.scheduledReleaseByEventId ?? new Map<number, number>()
  const unreleased: GameroomEvent[] = []
  const byRound = new Map<number, GameroomEvent[]>()

  for (const event of events) {
    if (event.is_released) {
      const key = event.released_round_number ?? OTHER_ROUND_KEY
      addToRoundBucket(byRound, key, event)
      continue
    }

    if (!includeUnreleased) {
      continue
    }

    const scheduledRound = scheduledReleaseByEventId.get(event.id)
    const stampedRound = event.released_round_number

    if (scheduledRound != null && scheduledRound >= 1) {
      addToRoundBucket(byRound, scheduledRound, event)
    } else if (stampedRound != null && stampedRound >= 1) {
      addToRoundBucket(byRound, stampedRound, event)
    } else {
      unreleased.push(event)
    }
  }

  const sortedKeys = [...byRound.keys()].sort((a, b) => {
    if (a === OTHER_ROUND_KEY) return 1
    if (b === OTHER_ROUND_KEY) return -1
    return a - b
  })

  const roundGroups: EventRoundGroup[] = sortedKeys.map((roundKey) => ({
    roundKey,
    roundLabel: getRoundLabel(roundKey),
    events: byRound.get(roundKey) ?? [],
  }))

  return { unreleased, roundGroups }
}

export function eventShowsPhaseLabel(event: GameroomEvent): boolean {
  return Boolean(event.released_phase_type && event.released_round_number)
}

export function getPhaseNumberLabel(
  phaseType: PhaseType | string | null | undefined,
  gameMode: TimerMode | string,
  roundNumber: number | null | undefined
): string | null {
  if (!phaseType || !roundNumber || roundNumber < 1) return null
  const types = getPhaseTypesForRound(gameMode as TimerMode, roundNumber)
  const index = types.indexOf(phaseType as PhaseType)
  if (index < 0) return null
  return `Phase ${index + 1}`
}

export function applyDefaultOpenRounds(
  prev: Record<number, boolean>,
  grouped: GroupedEvents,
  currentRound: number
): Record<number, boolean> {
  const next = { ...prev }
  if (grouped.unreleased.length > 0 && next[UNRELEASED_GROUP_KEY] === undefined) {
    next[UNRELEASED_GROUP_KEY] = true
  }
  for (const group of grouped.roundGroups) {
    if (next[group.roundKey] === undefined) {
      next[group.roundKey] =
        group.roundKey === currentRound ||
        (currentRound === 0 &&
          group.roundKey ===
            grouped.roundGroups[grouped.roundGroups.length - 1]?.roundKey)
    }
  }
  return next
}
